import { useEffect, useState } from 'react';

const DEFAULT_COOLDOWN_MS = 5000;

interface UseDownloadCooldownResult {
    downloadLoading: boolean;
    setDownloadLoading: (loading: boolean) => void;
    cooldownRemaining: number;
    getLastDownloadTime: () => number;
    setLastDownloadTime: (time: number) => void;
}

/**
 * 下载冷却时间管理 Hook
 * 使用 localStorage 持久化，页面刷新后仍然有效
 * @param storageKey localStorage 存储键名
 * @param cooldownMs 冷却时间（毫秒），默认 5000ms
 */
export function useDownloadCooldown(
    storageKey: string,
    cooldownMs: number = DEFAULT_COOLDOWN_MS
): UseDownloadCooldownResult {
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [cooldownRemaining, setCooldownRemaining] = useState<number>(0);

    const getLastDownloadTime = (): number => {
        const stored = localStorage.getItem(storageKey);
        return stored ? parseInt(stored, 10) : 0;
    };

    const setLastDownloadTime = (time: number) => {
        localStorage.setItem(storageKey, time.toString());
    };

    useEffect(() => {
        const checkCooldown = () => {
            const lastTime = getLastDownloadTime();
            if (lastTime > 0) {
                const remaining = Math.max(0, cooldownMs - (Date.now() - lastTime));
                const remainingSeconds = Math.ceil(remaining / 1000);
                setCooldownRemaining(remainingSeconds);
                if (remaining <= 0) {
                    setCooldownRemaining(0);
                    setDownloadLoading(false);
                    localStorage.removeItem(storageKey);
                }
            } else {
                setCooldownRemaining(0);
            }
        };
        checkCooldown();
        const interval = setInterval(checkCooldown, 1000);
        return () => clearInterval(interval);
    }, [storageKey, cooldownMs]);

    return {
        downloadLoading,
        setDownloadLoading,
        cooldownRemaining,
        getLastDownloadTime,
        setLastDownloadTime,
    };
}
